import { type AuthUser, useAuthStore } from '@/shared/stores/auth-store';

/**
 * Perfis de acesso no front — espelho de packages/db/src/perfis-de-acesso.ts
 * (mudou lá, muda aqui). Regra:
 *   - admin → tudo;
 *   - atendente → Atendimento + o que o perfil dele libera (sem perfil = só
 *     Atendimento).
 * O servidor barra de verdade; aqui só decide o que aparece (menu, telas).
 */

/** Ordem = ordem dos checkboxes no modal do perfil. */
export const TELAS = [
  'dashboard',
  'contatos',
  'historico',
  'agendadas',
  'campanhas',
  'canais',
  'chatbot',
  'configuracoes',
] as const;

export type Tela = (typeof TELAS)[number];

export const ROTULO_TELA: Record<Tela, string> = {
  dashboard: 'Dashboard',
  contatos: 'Contatos',
  historico: 'Histórico de Chats',
  agendadas: 'Mensagens Agendadas',
  campanhas: 'Campanhas',
  canais: 'Canais',
  chatbot: 'Chatbot',
  configuracoes: 'Configurações',
};

/** O que o usuário pode além do Atendimento. `verTodos` é chave do USUÁRIO
 *  ("Visualizar tickets de outros usuários dos seus departamentos"). */
export interface AcessoDoUsuario {
  perfilId: number | null;
  perfilNome: string | null;
  telas: Tela[];
  verTodos: boolean;
}

/** Formato da API (login, refresh, GET /me/acesso). */
export interface AcessoRaw {
  perfil_id: number | null;
  perfil_nome: string | null;
  telas: string[];
  ver_todos: boolean;
}

const ehTela = (t: string): t is Tela => (TELAS as readonly string[]).includes(t);

/** Converte o que a API mandou; chave desconhecida é descartada. */
export function acessoDaApi(raw: AcessoRaw | null | undefined): AcessoDoUsuario | null {
  if (!raw) return null;
  return {
    perfilId: raw.perfil_id,
    perfilNome: raw.perfil_nome,
    telas: (raw.telas ?? []).filter(ehTela),
    verTodos: raw.ver_todos === true,
  };
}

export function temTela(user: AuthUser | null | undefined, tela: Tela): boolean {
  if (!user) return false;
  if (user.profile === 'admin') return true;
  return user.acesso?.telas.includes(tela) ?? false;
}

/** "Visualizar todos" no Atendimento: admin sempre; atendente pela chave dele. */
export function podeVerTodos(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  if (user.profile === 'admin') return true;
  return user.acesso?.verTodos ?? false;
}

export function useTemTela(tela: Tela): boolean {
  return useAuthStore((s) => temTela(s.user, tela));
}

export function useVerTodos(): boolean {
  return useAuthStore((s) => podeVerTodos(s.user));
}
